import { H1Headline, H2Headline, H3Headline, Title, Unlink } from 'almanac-ui/icons'
import { ReactComponent as ArrowLeft } from 'assets/icons/arrow--left_16.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'
import cx from 'classnames'
import { EMAIL_REGEX_STR } from 'common/Prose/utils/link'
import { Button } from 'components/Button'
import { useOutsideClick } from 'components/Comments/hooks/useOutSideClick'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Level, calculateHeadings } from 'hooks/useTableOfContents'
import { SelectionAttributes } from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { MentionPrompt } from 'proseShared/prompts/MentionPrompt/MentionPrompt'
import * as React from 'react'
import { useRef, useState } from 'react'
import { getArticleUrl, getFolderUrl } from 'utils/urlUtils'
import * as validator from 'validator'
import Styles from './LinkModal.module.scss'
import { QueryOrLink } from './QueryOrLink'
import { LinkModalProps } from './Types'
import { LINK_MODAL_ID } from './constants'

const EMAIL_REGEX = new RegExp(`^mailto:${EMAIL_REGEX_STR}$`)

const checkUrl = (str: string) => validator.isURL(str) || EMAIL_REGEX.test(str)

const SEARCH_STEP = 0
const HEADERS_STEP = 1

export const LinkModal = ({
  initialText,
  initialQuery = '',
  callback,
  close,
  mentioningArticle,
  suggestRecentlyReadArticle,
  className = '',
  removeLink,
}: LinkModalProps) => {
  const [query, setQuery] = useState(initialQuery)
  const [text, setText] = useState(initialText)
  const [submitted, setSubmitted] = useState(false)
  const [linkError, setLinkError] = useState(false)
  const [step, setStep] = useState(SEARCH_STEP)
  const { isMobile } = useResponsiveDesign()
  const headings = calculateHeadings()

  const containerRef = useRef(null)

  const isEdit = initialQuery !== ''

  const submit = () => {
    if (query) {
      let transformedLink = query

      if (!query.startsWith('http://') && !query.startsWith('https://') && !query.startsWith('mailto:')) {
        transformedLink = `http://${query}`
      }

      callback({ link: transformedLink, text })
    }
  }

  const onSubmit = () => {
    setSubmitted(true)

    if (isEdit && !query) {
      callback({ text })
      close()
    } else if (checkUrl(query)) {
      submit()
      close()
    } else {
      setLinkError(true)
    }
  }

  const onChange = (event: any) => {
    const newLink = (event.target as HTMLInputElement).value
    setQuery(newLink)

    if (submitted) {
      setLinkError(!checkUrl(newLink))
    }
  }

  const onKeyDown = (event: any) => {
    if (event.key === 'Escape') {
      close()
    } else if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault()
      onSubmit()
    }
  }

  useOutsideClick(containerRef, close)

  const textLabelId = 'link-text-input'
  const linkLabelId = 'link-url-input'
  let textLabelNode: React.ReactElement | null = <label htmlFor={textLabelId}>Text</label>
  let linkLabelNode: React.ReactElement | null = <label htmlFor={linkLabelId}>Page or URL</label>
  let mobileModalHeaderNode = null
  if (isMobile) {
    mobileModalHeaderNode = (
      <div className={Styles.mobileHeaderWrapper}>
        <span className={Styles.mobileHeaderText}>Insert Link</span>
        <Button
          size="md"
          frontIcon={CloseIcon}
          variant="tertiary"
          className={cx(Styles.modalButton, Styles.noMargin)}
          onPress={close}
        />
      </div>
    )
    textLabelNode = null
    linkLabelNode = null
  }

  return (
    <div
      id={LINK_MODAL_ID}
      ref={containerRef}
      className={cx(className, Styles.modalWrapper, { [Styles.mobileLinkModal]: isMobile })}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {step === SEARCH_STEP && (
        <>
          {mobileModalHeaderNode}
          <div className={cx(Styles.modalBody)}>
            {textLabelNode}
            <input
              key="modalText"
              className={cx(Styles.input, Styles.textInput)}
              type="text"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
              }}
              onKeyDown={onKeyDown}
              placeholder="Enter display text"
              id={textLabelId}
            />

            {linkLabelNode}
            <QueryOrLink
              query={query}
              linkError={linkError}
              onChange={onChange}
              onKeyDown={onKeyDown}
              inputId={linkLabelId}
              onSubmit={onSubmit}
            />

            <div className={cx(Styles.mentionPrompt, { [Styles.mentionPromptError]: linkError })}>
              <MentionPrompt
                title="Link to..."
                showTitleWithNoResults={false}
                inputQuery={query}
                mentioningArticle={mentioningArticle}
                suggestRecentlyReadArticle={suggestRecentlyReadArticle}
                allowCreateNewFile={false}
                onSelect={({ type, key, url, title }: SelectionAttributes) => {
                  let link = ''

                  if (type === 'Article' && key) {
                    link = getArticleUrl(key)
                  } else if (type === 'Folder') {
                    link = getFolderUrl(url)
                  } else {
                    link = url
                  }

                  callback({ link, text: text === '' ? title : text })
                  close()
                }}
                onCancel={close}
                dropdownWidth="100%"
                overrideContainerClassname={Styles.mentionPromptContainer}
              />
            </div>
          </div>

          <div className={cx(Styles.modalFooter)}>
            <Button size="md" variant="tertiary" className={Styles.modalButton} onPress={() => setStep(HEADERS_STEP)}>
              Link to a heading in this doc
            </Button>
            {removeLink && (
              <Button size="md" variant="tertiary" onPress={removeLink} className={Styles.modalButton}>
                <Unlink />
                Remove
              </Button>
            )}
          </div>
        </>
      )}

      {step === HEADERS_STEP && (
        <>
          <div className={Styles.modalBackButtonWrapper}>
            <Button size="sm" variant="tertiary" className={Styles.modalButton} onPress={() => setStep(SEARCH_STEP)}>
              <ArrowLeft />
              Back
            </Button>
          </div>

          <div className={Styles.modalStep2Body}>
            {headings.length === 0 && <div className={Styles.noHeadings}>There are no headings in the document</div>}

            {headings.map(({ blockId, level, title }) => {
              return (
                <div
                  key={blockId}
                  className={Styles.headingRow}
                  onClick={() => {
                    callback({ link: `#${blockId}`, text: text === '' && title ? title : text })
                    close()
                  }}
                >
                  <div className={Styles.headingIcon}>{levelToIcon(level)}</div>

                  <div className={Styles.headingTitle}>{title}</div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

const levelToIcon = (level: Level) => {
  switch (level) {
    case 0:
      return <Title />
    case 1:
      return <H1Headline />
    case 2:
      return <H2Headline />
    case 3:
      return <H3Headline />
  }
}
